/**
 * Unified input: keyboard, mouse-drag camera look, wheel zoom, virtual touch controls and
 * game controllers (core/Gamepads.ts turns buttons into key codes and feeds `pad`).
 * Uses KeyboardEvent.code so controls are layout-independent.
 */
export class Input {
  private readonly down = new Set<string>();
  private readonly pressed = new Set<string>();
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  lookDX = 0;
  lookDY = 0;
  wheel = 0;
  /** Virtual joystick from touch controls, in [-1, 1]. */
  readonly stick = { x: 0, y: 0 };
  /** A game controller's left stick (and pedals when driving), in [-1, 1]. */
  readonly pad = { x: 0, y: 0 };
  /** Held virtual buttons (e.g. sprint toggle on touch). */
  readonly virtualHeld = new Set<string>();

  constructor(private readonly surface: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    surface.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    surface.addEventListener('wheel', this.onWheel, { passive: false });
    surface.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private static isTyping(e: Event): boolean {
    const t = e.target as HTMLElement | null;
    return (
      !!t &&
      (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
    );
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (Input.isTyping(e)) return;
    if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.code))
      e.preventDefault();
    if (!this.down.has(e.code)) this.pressed.add(e.code);
    this.down.add(e.code);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.down.delete(e.code);
  };

  private readonly onBlur = (): void => {
    this.down.clear();
    this.dragging = false;
  };

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return; // handled by TouchControls
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.dragging || e.pointerType === 'touch') return;
    this.lookDX += e.clientX - this.lastX;
    this.lookDY += e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };

  private readonly onPointerUp = (): void => {
    this.dragging = false;
  };

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.wheel += Math.sign(e.deltaY);
  };

  isDown(code: string): boolean {
    return this.down.has(code) || this.virtualHeld.has(code);
  }

  /** True once per key press (until endFrame). */
  wasPressed(code: string): boolean {
    return this.pressed.has(code);
  }

  /** Lets touch buttons inject a one-shot press. */
  press(code: string): void {
    this.pressed.add(code);
  }

  /** Movement intent: x = strafe right, y = forward. */
  moveAxis(): { x: number; y: number } {
    let x = this.stick.x + this.pad.x;
    let y = this.stick.y + this.pad.y;
    if (this.isDown('KeyW') || this.isDown('ArrowUp')) y += 1;
    if (this.isDown('KeyS') || this.isDown('ArrowDown')) y -= 1;
    if (this.isDown('KeyD') || this.isDown('ArrowRight')) x += 1;
    if (this.isDown('KeyA') || this.isDown('ArrowLeft')) x -= 1;
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    return { x, y };
  }

  addLook(dx: number, dy: number): void {
    this.lookDX += dx;
    this.lookDY += dy;
  }

  endFrame(): void {
    this.pressed.clear();
    this.lookDX = 0;
    this.lookDY = 0;
    this.wheel = 0;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    this.surface.removeEventListener('pointerdown', this.onPointerDown);
    this.surface.removeEventListener('wheel', this.onWheel);
  }
}
